import {
  Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges,
  TemplateRef, ViewChild
} from '@angular/core';
import {NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {CurrencyCode} from 'app/models/payroll-currency.model';
import {
  DealPayrollAssignmentTraining, DealPayrollGradeCode, PAYROLL_CALCULATION_METHOD, PayrollCalculationMethod,
  UserPayrollSettingsResponse
} from 'app/models/deal-payroll.model';
import {DealPayrollPositionOption} from 'app/models/deal-payroll-settings.adapter';
import {dealPayrollGradeKey} from 'app/models/deal-payroll-presentation';
import {DEAL_PAYROLL_ASSIGNMENT_WARNING_MODAL_OPTIONS}
  from './deal-payroll-user-settings.constants';
import {DealPayrollUserSettingsFacade} from './deal-payroll-user-settings.facade';
import {
  DealPayrollGradeOption, DealPayrollUserSettingsViewState
} from './deal-payroll-user-settings.types';

const GRADE_LOADING_KEY = 'LOADING';
const GRADE_SAVING_KEY = 'DEAL_PAYROLL_GRADE_SAVING';
const GRADE_SAVED_KEY = 'DEAL_PAYROLL_GRADE_SAVED';

@Component({
  selector: 'app-deal-payroll-user-settings',
  templateUrl: './deal-payroll-user-settings.component.html',
  styleUrls: ['./deal-payroll-user-settings.component.css'],
  providers: [DealPayrollUserSettingsFacade]
})
export class DealPayrollUserSettingsComponent implements OnChanges, OnDestroy {
  @Input() userId: string;
  @Input() fullSalary: number;
  @Input() salaryCurrency: CurrencyCode;
  @Output() settingsSaved: EventEmitter<UserPayrollSettingsResponse>;
  @ViewChild('dealPayrollAssignmentWarning') assignmentWarning: TemplateRef<Object>;

  readonly payrollCalculationMethod = PAYROLL_CALCULATION_METHOD;
  readonly viewState: DealPayrollUserSettingsViewState;
  gradeActionActive = false;
  private assignmentModal: NgbModalRef;

  constructor(
    private facade: DealPayrollUserSettingsFacade,
    private modalService: NgbModal
  ) {
    this.settingsSaved = facade.settingsSaved;
    this.viewState = facade.state;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.userId) {
      this.gradeActionActive = false;
      this.dismissAssignmentWarning();
      this.facade.changeUser(this.userId);
    }
  }

  ngOnDestroy(): void {
    this.dismissAssignmentWarning();
  }

  reload(preserveError: boolean = false): void {
    this.facade.reload(this.userId, preserveError);
  }

  resume(): void { this.facade.resume(); }

  get selectedGradeLabelKey(): string {
    return dealPayrollGradeKey(this.viewState.selectedGradeCode);
  }

  get gradeFeedbackKey(): string {
    if (!this.gradeActionActive) { return null; }
    if (this.viewState.loading) { return GRADE_LOADING_KEY; }
    if (this.viewState.saving) { return GRADE_SAVING_KEY; }
    return this.viewState.saveConfirmed ? GRADE_SAVED_KEY : null;
  }

  selectDealPayroll(): void {
    this.selectPayrollMethod(PAYROLL_CALCULATION_METHOD.PLANFIX_DEAL);
  }

  selectPayrollMethod(method: PayrollCalculationMethod): void {
    this.gradeActionActive = false;
    this.facade.selectPayrollMethod(method);
  }

  requestGradeAssignment(): void {
    this.gradeActionActive = true;
    this.facade.reviewAssignment(() => this.openAssignmentWarning());
  }

  confirmGradeSelection(close: () => void): void {
    if (!this.viewState.canStartDealAssignment) {
      return;
    }
    close();
    this.assignmentModal = null;
    this.facade.confirmGradeSelection();
  }

  selectPosition(positionKey: string): void {
    this.facade.selectPosition(positionKey);
  }

  selectGrade(gradeCode: DealPayrollGradeCode): void {
    this.facade.selectGrade(gradeCode);
  }

  isPayrollMethodSelected(method: PayrollCalculationMethod): boolean {
    return this.facade.state.isPayrollMethodSelected(method);
  }

  isPayrollMethodPending(method: PayrollCalculationMethod): boolean {
    return this.facade.state.isPayrollMethodPending(method);
  }

  trackByPosition(_index: number, option: DealPayrollPositionOption): string {
    return option.key;
  }

  trackByGrade(_index: number, option: DealPayrollGradeOption): string {
    return option.gradeCode;
  }

  trackByBlocker(_index: number, key: string): string {
    return key;
  }

  trackByTraining(_index: number, training: DealPayrollAssignmentTraining): string {
    return training.specializationId;
  }

  trackByDepartment(_index: number, department: {id: string}): string {
    return department.id;
  }

  private dismissAssignmentWarning(): void {
    const modal = this.assignmentModal;
    this.assignmentModal = null;
    if (modal) { modal.dismiss(); }
  }

  private openAssignmentWarning(): void {
    this.dismissAssignmentWarning();
    this.assignmentModal = this.modalService.open(
      this.assignmentWarning, DEAL_PAYROLL_ASSIGNMENT_WARNING_MODAL_OPTIONS);
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/views/view-user-profile/deal-payroll-user-settings/deal-payroll-user-settings.component.ts